import { useState, useMemo, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Input } from "@/shared/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { Label } from "@/shared/components/ui/label";
import { cn } from "@/lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  viewAllLabel?: string;
  className?: string;
  triggerClassName?: string;
  /** Forwarded to the trigger button so labels, FormControl and RHF focus work. */
  id?: string;
  disabled?: boolean;
  ref?: Ref<HTMLButtonElement>;
  "aria-label"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  "aria-describedby"?: string;
  "aria-required"?: boolean | "true" | "false";
}

/**
 * Single-select dropdown with search. Reusable for Section, Gender, etc.
 */
export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select",
  searchPlaceholder = "Search",
  viewAllLabel = "View All...",
  className,
  triggerClassName,
  id,
  disabled,
  ref,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
}: SearchableSelectProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Closing the list always resets the search box, so it reopens showing every option.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const resolvedPlaceholder = placeholder ?? t("actions.select");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("actions.search");
  const resolvedViewAllLabel = viewAllLabel ?? t("actions.viewAll");

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, search]);

  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? (value || resolvedPlaceholder);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          ref={ref}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          aria-required={ariaRequired}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-[#E0E0E0] bg-white px-3 py-2 text-sm text-[#333333] hover:bg-[#FAFAFA] disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName,
          )}
        >
          <span className={cn(!value && "text-[#9CA3AF]")}>
            {selectedLabel}
          </span>
          <span className="text-[#9CA3AF]">▾</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-[var(--radix-popover-trigger-width)] p-0 bg-white",
          className,
        )}
        align="start"
      >
        <div className="p-2 border-b border-[#E5E7EB]">
          <div className="relative">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={resolvedSearchPlaceholder}
              className="h-8 pl-2 pr-8 text-sm"
            />
            <Search className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
          </div>
        </div>
        <RadioGroup
          value={value}
          onValueChange={(v) => {
            onValueChange(v);
            handleOpenChange(false);
          }}
          className="max-h-[200px] overflow-y-auto p-2"
        >
          {filtered.map((opt) => (
            <div key={opt.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem
                value={opt.value}
                id={`searchable-${opt.value}`}
                className="text-[#007BFF] data-[state=checked]:bg-[#007BFF]"
              />
              <Label
                htmlFor={`searchable-${opt.value}`}
                className="cursor-pointer text-sm font-normal text-[#333333]"
              >
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {filtered.length === 0 && (
          <p className="px-2 py-2 text-sm text-[#6B7280]">{t("filter.noResults")}</p>
        )}
        <div className="border-t border-[#E5E7EB] p-2 ">
          <button
            type="button"
            className="text-xs text-[#3B82F6] hover:underline"
            onClick={() => setSearch("")}
          >
            {resolvedViewAllLabel}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
